import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Chip, CircularProgress, Divider, Paper, Typography } from '@mui/material';
import { ArrowLeft } from '@phosphor-icons/react';
import { readProposalPreviewFromStorage } from '../../../utils/openProposalPreview';
import { filterLayoutBlocks } from '../../../utils/proposalCraftUtils';
import { renderProposalBlock } from '../../Proposal/proposalBlockView';
import AgreementTermsViewer from '../../Proposal/AgreementTermsViewer';
import { getLineItemTotal, getUnitPriceWithTax } from '../../../utils/taxCalculations';

function formatCurrency(amount, currency = 'GBP') {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency
  }).format(amount);
}

function ProposalSupplierPreviewPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const k = searchParams.get('k');

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/supplier/agreements');
    }
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!k) {
      setError('No preview was requested.');
      setLoading(false);
      return;
    }
    const parsed = readProposalPreviewFromStorage(k);
    if (!parsed || typeof parsed !== 'object') {
      setError('Preview data was not found. Open preview again from the editor.');
      setLoading(false);
      return;
    }
    setData(parsed);
    setLoading(false);
  }, [k]);

  const rawBlocks = data?.content?.blocks;
  const layoutBlocks = useMemo(() => filterLayoutBlocks(rawBlocks || []), [rawBlocks]);
  const legacyFreeform = layoutBlocks.length === 0 && (!rawBlocks || rawBlocks.length === 0);

  const total = useMemo(() => {
    const items = data?.lineItems || [];
    if (items.length === 0) return 0;
    return Math.round(items.reduce((sum, item) => sum + getLineItemTotal(item), 0) * 100) / 100;
  }, [data?.lineItems]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
        <Button
          type="button"
          startIcon={<ArrowLeft size={18} />}
          onClick={handleBack}
          sx={{ mb: 2 }}
        >
          Back
        </Button>
        <Alert severity="error">{error || 'Preview unavailable.'}</Alert>
      </Box>
    );
  }

  const businessName = data.businessName || 'Your business';
  const currency = data.currency || 'GBP';

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 720, mx: 'auto' }}>
      <Button
        type="button"
        startIcon={<ArrowLeft size={18} />}
        onClick={handleBack}
        sx={{ mb: 2 }}
      >
        Back
      </Button>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Chip label="Preview" color="primary" size="small" />
        <Typography variant="body2" color="text.secondary">
          {data.kind === 'template'
            ? 'Template draft — not a live link. Recipient details are not shown.'
            : 'Draft preview — not a live link.'}
        </Typography>
      </Box>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Agreement from {businessName}
        </Typography>

        {layoutBlocks.length > 0 && (
          <>
            {layoutBlocks.map((block, idx) => renderProposalBlock(block, `root-${idx}`))}
            <Divider sx={{ my: 2 }} />
          </>
        )}

        {legacyFreeform && data.message && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {data.message}
            </Typography>
          </Box>
        )}

        {legacyFreeform && data.message ? <Divider sx={{ my: 2 }} /> : null}

        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Services & pricing
        </Typography>
        {(data.lineItems || []).length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No line items in this draft.
          </Typography>
        ) : (
          <>
            <Box component="ul" sx={{ listStyle: 'none', pl: 0, m: 0 }}>
              {data.lineItems.map((item) => {
                const qty = item.quantity ?? 1;
                const unitWithTax = getUnitPriceWithTax(item);
                const lt = getLineItemTotal(item);
                return (
                  <Box
                    key={item._id}
                    component="li"
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 1,
                      py: 1,
                      borderBottom: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1} flex={1} minWidth={0}>
                      <Typography variant="body2" component="span">
                        {item.description}
                        {item.isOptional ? ' (optional)' : ''}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 1 }}>
                        {qty}× {formatCurrency(unitWithTax, currency)}
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={500}>
                      {formatCurrency(lt, currency)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="h6">Total: {formatCurrency(total, currency)}</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Draft totals include all listed items and VAT where set. Couples may exclude optional extras before accepting.
            </Typography>
          </>
        )}

        {data.termsText ? (
          <>
            <Divider sx={{ my: 2 }} />
            <AgreementTermsViewer termsText={data.termsText} />
          </>
        ) : null}

        {(data.additionalInformationFields || []).length > 0 ? (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Additional information
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Couples complete these fields before signing. Shown here for reference only in preview.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {data.additionalInformationFields.map((field) => (
                <Box key={field.name}>
                  <Typography variant="body2" fontWeight={600}>
                    {field.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {field.type === 'textarea' ? 'Long text field' : 'Short text field'}
                    {field.placeholder ? ` · Placeholder: ${field.placeholder}` : ''}
                  </Typography>
                </Box>
              ))}
            </Box>
          </>
        ) : null}
      </Paper>
    </Box>
  );
}

export default ProposalSupplierPreviewPage;
